import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {NotificationState} from "./types";

import NotificationService from "./notification_services";

// Create a thunk to fetch notifications
export const fetchUserNotifications = createAsyncThunk(
    "notifications/fetchUserNotifications",
    async (query: any, {dispatch, rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            const data = await NotificationService.fetchNotifications(query);

            // Return response
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);


// Create a thunk to fetch a notification
export const fetchUserNotification = createAsyncThunk(
    "notifications/fetchUserNotification",
    async (id: string, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await NotificationService.fetchNotification(id);

            // Return response
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch notifications count
export const fetchNotificationsCount = createAsyncThunk(
    "notifications/fetchNotificationsCount",
    async (_, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await NotificationService.getNotificationsCount();

            // Return response
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to mark a notification as read
export const markNotificationAsRead = createAsyncThunk(
    "notifications/markNotificationAsRead",
    async (ids: string, {rejectWithValue}) => {
        try {
            // Make API request
            const data = await NotificationService.markNotificationAsRead(ids);

            // Return response
            return data.data;
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create initial state
const initialState: NotificationState = {
    notifications: [],
    notification: null,
    unread_count: 0,
    loading: false,
    error: null
}

// Create a slice
const notificationSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        clearNotification: (state) => {
            state.notification = null;
        },
        setNotification: (state, action) => {
            state.notification = action.payload;
        },
        setNotifications: (state, action) => {
            console.log("Setting notifications:", action.payload);
            state.notifications = action.payload.notifications || [];
            state.unread_count = action.payload.unread_count || 0;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserNotifications.fulfilled, (state, action) => {
                state.loading = false;
                state.notifications = Array.isArray(action.payload?.data)
                    ? action.payload.data
                    : Array.isArray(action.payload)
                        ? action.payload
                        : [];
            })
            .addCase(fetchUserNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchUserNotification.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserNotification.fulfilled, (state, action) => {
                state.loading = false;
                state.notification = action.payload;
            })
            .addCase(fetchUserNotification.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Add case for fetching notifications count
            .addCase(fetchNotificationsCount.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNotificationsCount.fulfilled, (state, action) => {
                state.loading = false;
                state.unread_count = action.payload;
            })
            .addCase(fetchNotificationsCount.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Add case for marking notification as read
            .addCase(markNotificationAsRead.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(markNotificationAsRead.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(markNotificationAsRead.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
    }
});

// Export actions
export const {clearNotification, setNotifications, setNotification} = notificationSlice.actions;

// Export reducer
export default notificationSlice.reducer;
